/**
 * UploadWidget — drag-and-drop + click-to-browse file selector for
 * `/documents`. Wraps a hidden `<input type="file">` and a large
 * click-target zone that also handles `dragenter/dragover/drop` events.
 *
 * Category + doc_type are chosen upstream (per-tab context) so the
 * widget just needs a `category` prop; it renders a small `<select>`
 * for `doc_type` populated from the taxonomy prop.
 *
 * Consent line is rendered above the file picker (user-approved copy).
 *
 * Progress is best-effort — axios `onUploadProgress` doesn't fire for
 * every browser on tiny files, so we also render an indeterminate
 * spinner state while awaiting the server response. If the actual
 * bytes upload in < 100ms the user will only see the spinner briefly.
 *
 * On success → calls `onUploaded(doc)` with the DocumentPublic body.
 * On failure → calls `onError(message)` with a normalised string.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { UploadCloud, Loader2, X, Check } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { pickDefaultDocType } from "./utils";

const MAX_FILE_MB = 25;

const DOC_TYPE_LABELS = {
  ein_letter: "EIN Letter",
  articles_of_organization: "Articles of Organization",
  articles_of_incorporation: "Articles of Incorporation",
  operating_agreement: "Operating Agreement",
  business_license: "Business License",
  certificate: "Certificate",
  tax_return: "Tax Return",
  profit_and_loss: "Profit & Loss",
  balance_sheet: "Balance Sheet",
  bank_statement: "Bank Statement",
  business_plan: "Business Plan",
  project_budget: "Project Budget",
  resume: "Resume",
  capability_statement: "Capability Statement",
  letter_of_support: "Letter of Support",
  certification: "Certification",
  uei_sam_documentation: "UEI / SAM Documentation",
  pdf: "PDF",
  image: "Image",
  supporting_document: "Supporting Document",
};

export function humanDocType(type) {
  return DOC_TYPE_LABELS[type] || type.replace(/_/g, " ");
}

export default function UploadWidget({
  category,
  docTypes = [],
  onUploaded,
  onError,
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [state, setState] = useState({ phase: "idle", progress: 0 });
  const [docType, setDocType] = useState(docTypes[0] || "");
  const [effectiveYear, setEffectiveYear] = useState("");
  const [tags, setTags] = useState("");

  // Sync docType when the parent's taxonomy list changes — critical
  // because taxonomy fetches asynchronously so on first mount
  // `docTypes` is empty and `useState`'s initial value never re-runs.
  // Pure resolver lives in `./utils.js` so it can be unit-tested.
  useEffect(() => {
    const next = pickDefaultDocType(docTypes, docType);
    if (next !== docType) setDocType(next);
  }, [docTypes, docType]);

  const disabled = state.phase === "uploading";

  const inlineDisabled = useMemo(() => docTypes.length === 0, [docTypes]);

  function reset() {
    setState({ phase: "idle", progress: 0 });
  }

  async function handleFile(file) {
    if (!file) return;
    if (!docType) {
      onError && onError("Choose a document type before uploading.");
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      onError && onError(`Files must be under ${MAX_FILE_MB} MB.`);
      return;
    }

    setState({ phase: "uploading", progress: 0 });
    const form = new FormData();
    form.append("consent_acknowledged", "true");
    form.append("category", category);
    form.append("doc_type", docType);
    form.append("display_name", file.name);
    if (tags.trim()) form.append("tags", tags.trim());
    if (effectiveYear && /^\d{4}$/.test(effectiveYear)) {
      form.append("effective_year", effectiveYear);
    }
    form.append("file", file, file.name);

    try {
      const res = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setState({
              phase: "uploading",
              progress: Math.round((evt.loaded / evt.total) * 100),
            });
          }
        },
      });
      setState({ phase: "success", progress: 100 });
      setTags("");
      setEffectiveYear("");
      onUploaded && onUploaded(res.data);
      setTimeout(reset, 1200);
    } catch (err) {
      setState({ phase: "idle", progress: 0 });
      const detail = err?.response?.data?.detail;
      if (detail?.code === "doc_count_cap_reached") {
        onError && onError({ code: detail.code, cap: detail.cap, tier: detail.tier });
      } else if (detail?.code === "doc_quota_reached") {
        onError && onError({ code: detail.code, quota_mb: detail.quota_mb, tier: detail.tier });
      } else if (detail?.code === "file_too_large") {
        onError && onError(`Files must be under ${detail.max_mb} MB.`);
      } else if (detail?.code === "mime_type_not_allowed") {
        onError && onError("This file type isn't supported. Allowed: PDF, DOCX, XLSX, PNG, JPG, HEIC.");
      } else if (detail?.code === "invalid_doc_type") {
        onError && onError("The document type doesn't match the category.");
      } else {
        onError && onError(formatApiError(err));
      }
    }
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <section
      data-testid={`documents-upload-widget-${category}`}
      className="rounded-3xl p-5"
      style={{
        background: "var(--v2-surface)",
        border: "1px solid var(--v2-border)",
      }}
    >
      <p
        data-testid="documents-upload-consent"
        className="text-[11px] leading-relaxed text-v2-textMuted mb-3"
      >
        By uploading, you confirm you have the right to store this document
        and that no sensitive data (e.g., Social Security Numbers, unmasked
        account numbers) has been included that you did not intend to share.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted">
          Document type
          <select
            data-testid={`documents-upload-doc-type-${category}`}
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            disabled={disabled || inlineDisabled}
            className="mt-1 w-full rounded-lg px-2 py-1.5 text-xs text-v2-text focus:outline-none focus:border-v2-accent"
            style={{
              background: "var(--v2-surface-subtle)",
              border: "1px solid var(--v2-border)",
              textTransform: "none",
              letterSpacing: 0,
              fontWeight: 400,
            }}
          >
            {docTypes.map((t) => (
              <option key={t} value={t}>
                {humanDocType(t)}
              </option>
            ))}
          </select>
        </label>

        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted">
          Effective year (optional)
          <input
            data-testid={`documents-upload-year-${category}`}
            type="text"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            placeholder="e.g., 2024"
            value={effectiveYear}
            onChange={(e) => setEffectiveYear(e.target.value.replace(/\D/g, ""))}
            disabled={disabled}
            className="mt-1 w-full rounded-lg px-2 py-1.5 text-xs text-v2-text focus:outline-none focus:border-v2-accent"
            style={{
              background: "var(--v2-surface-subtle)",
              border: "1px solid var(--v2-border)",
              textTransform: "none",
              letterSpacing: 0,
              fontWeight: 400,
            }}
          />
        </label>

        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted">
          Tags (optional, comma-sep)
          <input
            data-testid={`documents-upload-tags-${category}`}
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., q1, tax, 2024"
            disabled={disabled}
            className="mt-1 w-full rounded-lg px-2 py-1.5 text-xs text-v2-text focus:outline-none focus:border-v2-accent"
            style={{
              background: "var(--v2-surface-subtle)",
              border: "1px solid var(--v2-border)",
              textTransform: "none",
              letterSpacing: 0,
              fontWeight: 400,
            }}
          />
        </label>
      </div>

      <label
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        data-testid={`documents-upload-dropzone-${category}`}
        className={`block rounded-2xl px-6 py-8 text-center cursor-pointer transition-colors ${
          disabled ? "opacity-60 cursor-wait" : ""
        }`}
        style={{
          background: dragActive
            ? "rgba(167,139,250,0.10)"
            : "rgba(148,163,184,0.04)",
          border: `1px dashed ${
            dragActive ? "rgba(167,139,250,0.6)" : "var(--v2-border)"
          }`,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,image/heic,image/heif"
          className="hidden"
          disabled={disabled}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`documents-upload-input-${category}`}
        />
        {state.phase === "idle" && (
          <div className="flex flex-col items-center gap-2">
            <UploadCloud className="w-6 h-6 text-v2-accent" />
            <p className="text-sm text-v2-text font-semibold">
              Drop a file here or click to browse
            </p>
            <p className="text-[11px] text-v2-textMuted">
              PDF, DOCX, XLSX, PNG, JPG, HEIC. Up to {MAX_FILE_MB} MB.
            </p>
          </div>
        )}
        {state.phase === "uploading" && (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 text-v2-accent animate-spin" />
            <p className="text-sm text-v2-text font-semibold">
              Uploading… {state.progress > 0 ? `${state.progress}%` : ""}
            </p>
            <div
              className="w-full h-1.5 rounded-full overflow-hidden"
              style={{ background: "rgba(148,163,184,0.14)" }}
              data-testid={`documents-upload-progress-${category}`}
            >
              <div
                className="h-full transition-all"
                style={{
                  width: `${state.progress}%`,
                  background: "var(--v2-accent)",
                }}
              />
            </div>
          </div>
        )}
        {state.phase === "success" && (
          <div className="flex flex-col items-center gap-2">
            <Check className="w-6 h-6 text-emerald-400" />
            <p className="text-sm text-v2-text font-semibold">Upload complete.</p>
          </div>
        )}
      </label>
    </section>
  );
}
